/**
 * Role-access split (Timesheet Improvements #1) — which admin sections each
 * role sees.
 *
 * The admin hub lists ~100 surfaces across 9 themed groups; not every role
 * needs all of them. This maps the active role to the admin groups it should
 * see, so the hub (and later the worker nav) shows a focused set per role.
 *
 * Keyed on the actual role strings in use: the `SystemRole` enum
 * (GlobalAdmin / Lead / Timekeeper / ...) AND the legacy free-form DB values
 * the demo carries ('Supervisor' / 'Staff'). Same approach as the role sets in
 * manufacturingAccess.ts.
 *
 * DESIGN: allow-list per restrictive role; FAIL-OPEN for unknown roles (an
 * unrecognized role sees everything rather than getting stranded with an empty
 * hub). 'Staff' (individual contributors) is the one explicit empty case.
 *
 * This config is intentionally simple to redline: edit a role's array to move
 * groups between roles. The group strings MUST match the `label` values in
 * adminGroups.tsx exactly (a unit test asserts this).
 */

/** Roles that see every admin group (god mode / org-wide read). OrgAdmin = the
 *  customer top admin (recast from the old customer GlobalAdmin); GlobalAdmin =
 *  the platform master dev. Both get the full admin hub. */
const FULL_ACCESS_ROLES: ReadonlySet<string> = new Set(['GlobalAdmin', 'OrgAdmin', 'GlobalViewer'])

/**
 * Role → visible admin group labels. Keyed on the task-oriented (verb) buckets
 * in `adminGroups.tsx`: Review & Approve · Check Compliance · Protect Data &
 * Privacy · Run Cost & Billing · Run Production · Manage People & Time Off ·
 * Connect Systems · Configure. Roles NOT listed here fall through to fail-open
 * (see visibleAdminGroupLabels). 'Staff' is explicitly empty.
 */
export const ADMIN_GROUP_VISIBILITY: Record<string, readonly string[]> = {
  // Setup / integrations admin.
  ConfigAdmin: ['Configure', 'Connect Systems'],
  // Manager / supervisor (enum 'Lead' + legacy 'Supervisor').
  Lead: ['Review & Approve', 'Manage People & Time Off', 'Run Production'],
  Supervisor: ['Review & Approve', 'Manage People & Time Off', 'Run Production'],
  // Payroll / cost accounting.
  Timekeeper: ['Review & Approve', 'Run Cost & Billing', 'Manage People & Time Off'],
  // Compliance / audit (read-heavy) — incl. the privacy/security pages.
  ComplianceOfficer: ['Check Compliance', 'Protect Data & Privacy', 'Review & Approve'],
  Auditor: ['Check Compliance', 'Protect Data & Privacy', 'Review & Approve'],
  // Individual contributors — the admin hub isn't for them.
  Staff: [],
}

/**
 * The admin groups visible to `role`. Returns the sentinel 'all' when the role
 * sees everything (full-access roles, or any unknown/None role — fail-open).
 */
export function visibleAdminGroupLabels(
  role: string | null | undefined
): 'all' | ReadonlySet<string> {
  if (!role) return 'all'
  if (FULL_ACCESS_ROLES.has(role)) return 'all'
  const allowed = ADMIN_GROUP_VISIBILITY[role]
  if (!allowed) return 'all' // unknown role (incl. 'None') → fail-open, never strand
  return new Set(allowed)
}

/** Filter a list of admin groups (anything with a `label`) to those `role` sees. */
export function filterAdminGroupsByRole<T extends { label: string }>(
  groups: readonly T[],
  role: string | null | undefined
): T[] {
  const visible = visibleAdminGroupLabels(role)
  if (visible === 'all') return [...groups]
  return groups.filter((g) => visible.has(g.label))
}

// ── Worker sidebar nav (#1 P2) ───────────────────────────────────────────────

/**
 * Worker-nav item keys that are manager / supervisor surfaces, not individual-
 * contributor ones. Hidden from ICs so a plain worker's sidebar stays focused
 * on their own time. Keys match the nav items in Sidebar.tsx.
 */
export const MANAGER_WORKER_NAV_KEYS: ReadonlySet<string> = new Set([
  'ts-floor-activity',
  'ts-exceptions',
  'ts-call-out-roster',
])

/**
 * Roles that should NOT see manager surfaces in the worker nav — the
 * individual-contributor roles. Same deny-list + fail-open shape as
 * CONTRACT_BURN_HIDDEN_ROLES in manufacturingAccess.ts (keyed on the real DB
 * strings): anyone not explicitly an IC keeps seeing them.
 */
const NON_MANAGER_ROLES: ReadonlySet<string> = new Set(['Staff', 'None'])

/** Whether `role` should see manager surfaces in the worker sidebar nav. */
export function canSeeManagerWorkerNav(role: string | null | undefined): boolean {
  if (!role) return true // fail-open
  return !NON_MANAGER_ROLES.has(role)
}

/** Filter worker-nav items (anything with a `key`) by role: drops manager
 *  surfaces for individual-contributor roles, keeps everything otherwise. */
export function filterWorkerNavByRole<T extends { key: string }>(
  items: readonly T[],
  role: string | null | undefined
): T[] {
  if (canSeeManagerWorkerNav(role)) return [...items]
  return items.filter((i) => !MANAGER_WORKER_NAV_KEYS.has(i.key))
}

// ── Time-approval surface (Approval Queue) ───────────────────────────────────

/**
 * RBAC system roles that can approve worker time. Drives the first-class
 * Approval Queue entry in the worker nav — shown only to approvers. The Sidebar
 * ALSO treats a persona who has direct reports as an approver (the demo signal),
 * so this is the real-auth half of the gate.
 */
const TIME_APPROVER_ROLES: ReadonlySet<string> = new Set([
  'GlobalAdmin', 'OrgAdmin', 'Lead', 'Supervisor', 'Timekeeper',
])

/** Whether `role` (an RBAC system role) can approve worker time. Fail-CLOSED:
 *  an unknown/empty role does NOT get the approver surface on its own. */
export function canApproveTimeRole(role: string | null | undefined): boolean {
  return !!role && TIME_APPROVER_ROLES.has(role)
}

// ── Worker sidebar nav by work category (office vs manufacturing) ─────────────

/**
 * Worker-nav keys for the shop-floor self-service surfaces — kiosk clock-in
 * (`ts-wand`) plus the worker's own reconciled manufacturing day/week. These only
 * apply to workers whose `workCategory` is 'manufacturing'; office workers enter
 * time on the grid (`/timesheet/my`) and never clock in at a kiosk.
 */
export const MANUFACTURING_WORKER_NAV_KEYS: ReadonlySet<string> = new Set([
  'ts-wand',
  'ts-mfg-day',
  'ts-mfg-week',
])

/**
 * Worker-nav keys for OFFICE-only time-entry surfaces — the office timesheet grid
 * plus utilization. Manufacturing workers enter time on the floor (kiosk / My
 * Manufacturing Day), so these are dropped for them. The remaining worker-nav
 * surface (My PTO Calendar) is universal; PTO balance, compliance, and data
 * requests now live under Admin & Reporting, not the worker nav.
 */
export const OFFICE_WORKER_NAV_KEYS: ReadonlySet<string> = new Set([
  'ts-my',
  'ts-my-utilization',
  'ts-my-office-hourly',
])

/** Filter worker-nav items by work category — bidirectional. Office workers lose
 *  the manufacturing self-service surfaces; manufacturing workers lose the
 *  office-only time-entry surfaces (grid + utilization), so a floor worker's nav
 *  is genuinely manufacturing-only. */
export function filterWorkerNavByWorkCategory<T extends { key: string }>(
  items: readonly T[],
  workCategory: string | null | undefined
): T[] {
  if (workCategory === 'manufacturing') {
    return items.filter((i) => !OFFICE_WORKER_NAV_KEYS.has(i.key))
  }
  return items.filter((i) => !MANUFACTURING_WORKER_NAV_KEYS.has(i.key))
}
